import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    TextField,
    MenuItem,
    Grid,
    Skeleton,
    useTheme,
    useMediaQuery,
    Fab
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { invoicesAPI } from '../api/api';
import { formatCurrency, formatDate, LABELS } from '../config';
import AdvancedFilters from './common/AdvancedFilters';
import { useAuth } from '../contexts/AuthContext';

const Invoices = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { user } = useAuth();

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        status: '',
        search: '',
        sortBy: 'invoice_date',
        sortOrder: 'DESC'
    });

    useEffect(() => {
        fetchInvoices();
    }, [filters]);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const response = await invoicesAPI.getAll(filters);
            if (response.data.success) {
                setInvoices(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            toast.error('Failed to load invoices');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Invoice?',
            text: 'This cannot be undone if there are no payments.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete'
        });

        if (result.isConfirmed) {
            try {
                await invoicesAPI.delete(id);
                toast.success('Invoice deleted');
                fetchInvoices();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Failed to delete invoice');
            }
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PAID': return 'success';
            case 'PARTIAL': return 'warning';
            case 'PENDING': return 'error';
            default: return 'default';
        }
    };

    // Mobile Card View
    const InvoiceCard = ({ invoice }) => (
        <Card sx={{ mb: 2 }} className="hover-lift">
            <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                        <Typography variant="subtitle2" color="primary" fontWeight={600}>
                            {invoice.invoice_number}
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {invoice.customer_name}
                        </Typography>
                    </Box>
                    <Chip
                        label={LABELS.paymentStatus[invoice.payment_status]}
                        size="small"
                        color={getStatusColor(invoice.payment_status)}
                    />
                </Box>

                <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body2">{formatDate(invoice.invoice_date)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Due Date</Typography>
                        <Typography variant="body2">{formatDate(invoice.due_date) || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(invoice.total_amount)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Balance Due</Typography>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            color={invoice.balance_due > 0 ? 'error.main' : 'success.main'}
                        >
                            {formatCurrency(invoice.balance_due)}
                        </Typography>
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                    <IconButton size="small" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                        <ViewIcon fontSize="small" />
                    </IconButton>
                    {user && user.role !== 'VIEWER' && (
                        <IconButton size="small" color="error" onClick={() => handleDelete(invoice.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                    Invoices
                </Typography>
                {!isMobile && user && user.role !== 'VIEWER' && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/invoices/new')}
                    >
                        New Invoice
                    </Button>
                )}
            </Box>

            <AdvancedFilters
                filters={filters}
                onFilterChange={setFilters}
                configs={{
                    search: true,
                    dateRange: true,
                    status: true,
                    sortBy: [
                        { label: 'Date', value: 'invoice_date' },
                        { label: 'Amount', value: 'total_amount' },
                        { label: 'Invoice #', value: 'invoice_number' },
                        { label: 'Due Date', value: 'due_date' }
                    ]
                }}
                data={{
                    statuses: [
                        { label: 'Pending', value: 'PENDING' },
                        { label: 'Partial', value: 'PARTIAL' },
                        { label: 'Paid', value: 'PAID' }
                    ]
                }}
            />

            {/* Content */}
            {loading ? (
                [...Array(5)].map((_, i) => <Skeleton key={i} height={100} sx={{ mb: 1 }} />)
            ) : isMobile ? (
                <Box>
                    {invoices.map((invoice) => (
                        <InvoiceCard key={invoice.id} invoice={invoice} />
                    ))}
                    {invoices.length === 0 && (
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <Typography color="text.secondary">No invoices found</Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            ) : (
                <Card>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Invoice #</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Paid</TableCell>
                                    <TableCell align="right">Balance</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoices.map((invoice) => (
                                    <TableRow key={invoice.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} color="primary">
                                                {invoice.invoice_number}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                                        <TableCell>
                                            <Typography fontWeight={500}>{invoice.customer_name}</Typography>
                                            {invoice.customer_phone && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {invoice.customer_phone}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(invoice.total_amount)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>
                                            {formatCurrency(invoice.amount_paid)}
                                        </TableCell>
                                        <TableCell align="right" sx={{
                                            color: invoice.balance_due > 0 ? 'error.main' : 'success.main',
                                            fontWeight: 600
                                        }}>
                                            {formatCurrency(invoice.balance_due)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={LABELS.paymentStatus[invoice.payment_status]}
                                                size="small"
                                                color={getStatusColor(invoice.payment_status)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                            {user && user.role !== 'VIEWER' && (
                                                <IconButton size="small" color="error" onClick={() => handleDelete(invoice.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {invoices.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No invoices found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Mobile FAB */}
            {isMobile && user && user.role !== 'VIEWER' && (
                <Fab
                    color="primary"
                    sx={{ position: 'fixed', bottom: 72, right: 16 }}
                    onClick={() => navigate('/invoices/new')}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default Invoices;
